import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "../api_client";
import L from "leaflet";
import { addCourseLegend, addCourseMarkers } from "../utils/courseMarkers";

const DEFAULT_CENTER = [53.38, -1.47];

export default function Tracking({ eventId }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const polylineRef = useRef(null);
  const courseLayerRef = useRef(null);
  const courseLegendRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [course, setCourse] = useState({ start: null, finish: null });
  const [mapLoading, setMapLoading] = useState(false);

  const clearPath = () => {
    const map = mapInstanceRef.current;
    if (map && polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }
  };

  const showEntrantPath = async (entrantId) => {
    const map = mapInstanceRef.current;
    if (!map || !eventId || !entrantId) return;

    clearPath();

    try {
      const data = await apiClient.entrants.getLocation(eventId, entrantId);
      const pathPoints = data?.points || [];
      if (pathPoints.length === 0) return;

      const latlngs = pathPoints.map((p) => [p.lat, p.lng]);
      if (latlngs.length === 1) {
        map.setView(latlngs[0], 15);
        return;
      }

      const polyline = L.polyline(latlngs, { weight: 4, color: "#2563eb" }).addTo(map);
      polylineRef.current = polyline;
      map.fitBounds(latlngs, { padding: [30, 30] });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!eventId) return;

    const loadLocations = async () => {
      setMapLoading(true);
      try {
        const response = await apiClient.tracking.getLocations(eventId);
        const entrants = response.entrants || [];

        setCourse({
          start: response.start || null,
          finish: response.finish || null,
        });

        setPoints(
          entrants.map((data) => ({
            id: data.id,
            lat: data.lat,
            long: data.long,
            number: data.number,
            name: data.name,
            timestamp: new Date(data.time),
          }))
        );
      } catch (err) {
        console.error(err);
        setPoints([]);
        setCourse({ start: null, finish: null });
      } finally {
        setMapLoading(false);
      }
    };

    loadLocations();
  }, [eventId]);

  const hasMapContent = points.length > 0 || course.start || course.finish;

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current) return;
    if (mapLoading) return;
    if (!hasMapContent) return;

    const initial =
      points[0]
        ? [points[0].lat, points[0].long]
        : course.start
          ? [course.start.lat, course.start.lng]
          : course.finish
            ? [course.finish.lat, course.finish.lng]
            : DEFAULT_CENTER;

    const map = L.map(mapRef.current).setView(initial, 13);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      clearPath();
      if (courseLayerRef.current) {
        map.removeLayer(courseLayerRef.current);
        courseLayerRef.current = null;
      }
      if (courseLegendRef.current) {
        map.removeControl(courseLegendRef.current);
        courseLegendRef.current = null;
      }
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current = {};
    };
  }, [mapLoading, hasMapContent]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const bounds = [];

    const courseLatLngs = addCourseMarkers(map, course, courseLayerRef);
    addCourseLegend(map, course, courseLegendRef);
    courseLatLngs.forEach((ll) => bounds.push([ll.lat, ll.lng]));

    points.forEach((p) => {
      const position = [p.lat, p.long];
      bounds.push(position);

      const icon = L.divIcon({
        className: "numbered-marker",
        html: `<div class="marker-circle">${p.number}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });

      const lastSeen = p.timestamp.toLocaleTimeString();
      const popupContent = `<strong>${p.name}</strong><br/>Last seen: ${lastSeen}`;

      if (markersRef.current[p.id]) {
        const marker = markersRef.current[p.id];
        marker.setLatLng(position).setIcon(icon);
        marker.getPopup().setContent(popupContent);
        marker.off("click");
        marker.on("click", () => showEntrantPath(p.id));
      } else {
        const marker = L.marker(position, { icon }).addTo(map);
        marker.bindPopup(popupContent);
        marker.on("click", () => showEntrantPath(p.id));
        markersRef.current[p.id] = marker;
      }
    });

    if (bounds.length === 1) {
      map.setView(bounds[0], 15);
    } else if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [points, course, eventId]);

  return (
    <>
      {mapLoading && (
        <div style={{ textAlign: "center" }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: "15px" }}>Loading tracking data...</p>
        </div>
      )}

      {!mapLoading && !hasMapContent && (
        <p className="text-muted">No location data yet. Add start/finish locations or wait for entrant tracking.</p>
      )}

      {!mapLoading && hasMapContent && (
        <div
          ref={mapRef}
          style={{
            height: "450px",
            borderRadius: "10px",
            marginBottom: "30px",
          }}
        />
      )}
      <style>{`
        .numbered-marker {
          background: none;
          border: none;
        }
        .marker-circle {
          width: 28px;
          height: 28px;
          background: #2563eb;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        .course-marker {
          background: none;
          border: none;
        }
      `}</style>
    </>
  );
}
